import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  useInView,
  AnimatePresence,
} from 'framer-motion';
import { NavBar } from './ui/TubelightNavbar.jsx';
import { Logo } from './Logo';
import { Footer } from './ui/Footer';
import LoginModal from './auth/LoginModal';
import { CinematicBackground } from './ui/CinematicBackground';

/* ═══════════════════════════════════════════════════════════════
   MODELS PAGE - Immersive edition
   ───────────────────────────────────────────────────────────────
   One continuous scroll. Fraunces + JetBrains Mono + violet.
   Real data from /api/models. Five acts:
     1. Hero with orbital logo constellation
     2. Pinned stats moment (category counts count up)
     3. Provider galaxy (13 provider cards)
     4. Live lineup (tabbed, searchable table of all models)
     5. Manifesto + CTA
   ═══════════════════════════════════════════════════════════════ */

// ─── Design tokens - mirrors SolutionsPage ──────────────────────
const SP = {
  bg:          'transparent',
  canvas:      'rgba(0,0,0,0.4)',
  canvasCool:  'rgba(0,0,0,0.6)',
  ink:         '#FFFFFF',
  inkSoft:     'rgba(255,255,255,0.9)',
  muted:       'rgba(255,255,255,0.7)',
  subtle:      'rgba(255,255,255,0.4)',
  faint:       'rgba(255,255,255,0.2)',
  line:        'rgba(255,255,255,0.1)',
  lineSoft:    'rgba(255,255,255,0.05)',
  accentA:     '#7C3AED',
  accentB:     '#A78BFA',
  accentC:     '#C4B5FD',
  accentGrad:  'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
  accentTint:  'rgba(124,58,237,0.1)',
  accentTintB: 'rgba(124,58,237,0.2)',
  accentDeep:  '#C4B5FD',
};
const SERIF = '"Nasalization", "Instrument Serif", Georgia, serif';
const MONO  = '"JetBrains Mono", ui-monospace, Menlo, monospace';
// Geometric tech face for model names. Nasalization is commercial; Orbitron and
// Space Grotesk are open fallbacks that read in the same sci-fi register.
const TECH  = '"Nasalization", "Orbitron", "Space Grotesk", ui-sans-serif, system-ui, sans-serif';

// ─── Provider logo map (copied from legacy ModelsPage) ──────────
const PROVIDER_LOGOS = {
  Anthropic: '/claude-ai-icon.svg',
  OpenAI:    '/images/logos/openai.svg',
  Google:    '/google-icon.svg',
  Meta:      '/meta-icon.svg',
  xAI:       '/xai-icon.svg',
  Mistral:   '/mistral-icon.svg',
  Cohere:    '/images/logos/cohere.svg',
  DeepSeek:  '/deepseek-logo-icon.svg',
  Qwen:      '/qwen-ai-icon.svg',
  Microsoft: '/microsoft-icon.svg',
  Perplexity:'/images/logos/perplexity.svg',
  AI21:      '/ai21-icon.svg',
  ByteDance: '/bytedance.svg',
  'Black Forest Labs': '/flux.svg',
  Runway:    '/runway.svg',
  Kling:     '/kling-icon.svg',
  Ideogram:  '/ideogram.svg',
  Suno:      '/suno.svg',
};

// Normalise provider labels that come from the API (lower-case / slash paths).
const PROVIDER_ALIASES = {
  'anthropic': 'Anthropic', 'openai': 'OpenAI', 'google': 'Google',
  'meta': 'Meta', 'meta-llama': 'Meta', 'x-ai': 'xAI', 'xai': 'xAI',
  'mistral': 'Mistral', 'mistralai': 'Mistral', 'cohere': 'Cohere',
  'deepseek': 'DeepSeek', 'qwen': 'Qwen', 'microsoft': 'Microsoft',
  'perplexity': 'Perplexity', 'ai21': 'AI21', 'bytedance': 'ByteDance',
  'bytedance-seed': 'ByteDance', 'flux': 'Black Forest Labs',
  'runway': 'Runway', 'kling': 'Kling', 'ideogram': 'Ideogram', 'suno': 'Suno',
};

const normaliseProvider = (p) => {
  if (!p) return 'Unknown';
  return PROVIDER_ALIASES[p.toLowerCase()] || p;
};

// Fallback counts used before the API responds, so the page never reads as empty.
const FALLBACK_STATS = { chat: 204, image: 18, video: 12, music: 8 };
const FALLBACK_TOTAL = FALLBACK_STATS.chat + FALLBACK_STATS.image + FALLBACK_STATS.video + FALLBACK_STATS.music;

/* ─── Responsive hook ─────────────────────────────────────────── */
function useBreakpoint() {
  const read = () => {
    if (typeof window === 'undefined') return { isMobile: false, isTablet: false, isDesktop: true };
    const w = window.innerWidth;
    return { isMobile: w < 640, isTablet: w >= 640 && w < 1024, isDesktop: w >= 1024 };
  };
  const [bp, setBp] = useState(read);
  useEffect(() => {
    const onResize = () => setBp(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return bp;
}

/* ─── Live model data hook ────────────────────────────────────── */
function useModels() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const API_URL = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';

    fetch(`${API_URL}/api/models`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) { setLoading(false); return; }

        // Worker shape: { chatModels: {...}, imageModels: {...}, ... }
        // Local shape:  { models: [ { category, ... } ] }
        const pick = (keyed, cat) => {
          if (keyed) return Object.values(keyed);
          return (data.models || []).filter(m =>
            m.category === cat || (Array.isArray(m.functions) && m.functions.includes(cat))
          );
        };

        const chat  = pick(data.chatModels,  'chat');
        const image = pick(data.imageModels, 'image');
        const video = pick(data.videoModels, 'video');
        const music = pick(data.musicModels, 'music');

        const shape = (m, fn) => {
          const promptPerToken = parseFloat(m.pricing_input  || m.pricing?.prompt     || 0);
          const complPerToken  = parseFloat(m.pricing_output || m.pricing?.completion || 0);
          const inputPerM      = promptPerToken * 1e6;
          const outputPerM     = complPerToken  * 1e6;
          let name             = m.name || m.id || m.model_id || '';
          // Strip "Anthropic: " etc. prefixes
          name = name.replace(/^(Anthropic|OpenAI|Google|Meta|xAI|Mistral|Cohere|DeepSeek|Qwen|Microsoft|Perplexity|AI21):\s*/, '');
          const provider = normaliseProvider(m.provider);
          const ctx = m.context_window || m.contextWindow || null;
          return {
            id:         m.id || m.model_id || name,
            fn,
            provider,
            name,
            ctxWindow:  ctx,
            inputPerM:  fn === 'Chat' ? inputPerM  : null,
            outputPerM: fn === 'Chat' ? outputPerM : null,
          };
        };

        const all = [
          ...chat.map(m  => shape(m, 'Chat')),
          ...image.map(m => shape(m, 'Image')),
          ...video.map(m => shape(m, 'Video')),
          ...music.map(m => shape(m, 'Music')),
        ];

        if (!cancelled) {
          setModels(all);
          setLoading(false);
        }
      })
      .catch(err => {
        console.warn('[ModelsPage] /api/models failed:', err?.message);
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return { models, loading };
}

/* ═══════════════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════════════ */
export function ModelsPage({ embedded = false }) {
  const [showLoginModal, setShowLoginModal] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { models, loading } = useModels();

  // Totals and per-category counts used throughout the page.
  const stats = useMemo(() => {
    if (!models.length) return { total: FALLBACK_TOTAL, ...FALLBACK_STATS };
    const c = { chat: 0, image: 0, video: 0, music: 0 };
    models.forEach(m => { c[m.fn.toLowerCase()]++; });
    return { total: models.length, ...c };
  }, [models]);

  // Provider aggregation - for galaxy + lineup tabs.
  const providerBuckets = useMemo(() => {
    const buckets = {};
    models.forEach(m => {
      if (!buckets[m.provider]) buckets[m.provider] = [];
      buckets[m.provider].push(m);
    });
    return Object.entries(buckets)
      .map(([provider, list]) => ({ provider, list, count: list.length }))
      .sort((a, b) => b.count - a.count);
  }, [models]);

  const navItems = [
    { name: 'Home',       url: '/',          icon: 'home' },
    { name: 'Solutions',  url: '/solutions' },
    { name: 'Models',     url: '/models'    },
    { name: 'Enterprise', url: '/enterprise'},
  ];

  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 80, damping: 28, mass: 0.6 });

  // Embedded mode (legacy, used inside other pages) - render bare lineup only.
  if (embedded) {
    return (
      <div style={{ padding: '40px 20px', background: SP.bg }}>
        <LineupSection models={models} loading={loading} />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      position: 'relative',
      color: SP.ink,
      background: SP.bg,
      overflowX: 'hidden',
    }}>
      <CinematicBackground />
      <div className="fixed top-4 left-4 md:top-6 md:left-8 z-50"><Logo /></div>
      <NavBar items={navItems} onSignInClick={() => setShowLoginModal(true)} />
      <ScrollProgressBar progress={smoothProgress} />

      <HeroScene stats={stats} />
      <StatsMoment stats={stats} loading={loading} />
      <ProviderGalaxy buckets={providerBuckets} loading={loading} />
      <LineupSection models={models} loading={loading} />
      <Manifesto />
      <CtaSection onSignInClick={() => setShowLoginModal(true)} />

      <Footer />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </div>
  );
}
export default ModelsPage;



function ScrollProgressBar({ progress }) {
  const scaleX = useTransform(progress, [0, 1], [0, 1]);
  return (
    <motion.div style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: '2px',
      background: SP.accentGrad, transformOrigin: 'left', scaleX, zIndex: 200,
    }} />
  );
}

/* ═══════════════════════════════════════════════════════════════
   HERO - giant typographic headline + dual-row provider carousel.
   ═══════════════════════════════════════════════════════════════ */
function HeroScene({ stats }) {
  const { isMobile, isTablet } = useBreakpoint();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade   = useTransform(scrollYProgress, [0, 0.7, 1], [1, 1, 0]);

  return (
    <section ref={ref} style={{
      position: 'relative',
      textAlign: 'center',
      maxWidth: '1280px',
      margin: '0 auto',
      padding: isMobile ? '120px 20px 72px' : isTablet ? '150px 32px 96px' : '180px 40px 120px',
      zIndex: 1,
      overflow: 'visible',
    }}>
      <motion.div style={{ y: titleY, opacity: fade }}>
        <h1 style={{
          fontFamily: SERIF,
          fontSize: 'clamp(52px, 8.4vw, 124px)',
          lineHeight: 1.05,
          letterSpacing: '-0.04em',
          color: SP.ink,
          fontWeight: 400,
          margin: '0 auto 40px',
          maxWidth: '1160px',
          paddingBottom: '0.12em',
        }}>
          <HeroWord i={0}>One</HeroWord>
          <HeroWord i={1}>login.</HeroWord>
          <br />
          <HeroWord i={2}>Every</HeroWord>
          <HeroEm i={3}>frontier model</HeroEm>
          <HeroWord i={4}>.</HeroWord>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.7 }}
          style={{
            fontSize: 'clamp(15px, 1.6vw, 19px)',
            lineHeight: 1.6,
            color: SP.muted,
            maxWidth: '680px',
            margin: '0 auto 48px',
            fontWeight: 400,
            padding: isMobile ? '0 8px' : 0,
          }}
        >
          {stats.total}+ models from the thirteen labs that matter — routed,
          metered, and billed in one unified account. Pick the one you want,
          or let DAGGPT choose.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          style={{ display: 'inline-flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}
        >
          <PrimaryCTA href="/chat">Try a model</PrimaryCTA>
          <GhostCTA href="#lineup" trailing="→">Browse the library</GhostCTA>
        </motion.div>
      </motion.div>

      {/* Dual-row provider logo carousel - drifts across the hero footer.
          Row 1 drifts left-to-right, row 2 right-to-left for a layered feel. */}
      <ProviderLogoCarousel />
    </section>
  );
}

/** All-providers dual-row marquee. Shown under the hero to fill the empty
 *  space with real brand identity. Counter-drifting rows for depth. */
function ProviderLogoCarousel() {
  const { isMobile } = useBreakpoint();

  // Deliberate two-row split - keeps both lines balanced and visually busy.
  const rowA = [
    { name: 'Anthropic',         src: '/claude-ai-icon.svg'       },
    { name: 'OpenAI',            src: '/images/logos/openai.svg'  },
    { name: 'Google',            src: '/google-icon.svg'          },
    { name: 'Meta',              src: '/meta-icon.svg'            },
    { name: 'xAI',               src: '/xai-icon.svg'             },
    { name: 'Mistral',           src: '/mistral-icon.svg'         },
    { name: 'DeepSeek',          src: '/deepseek-logo-icon.svg'   },
    { name: 'Qwen',              src: '/qwen-ai-icon.svg'         },
    { name: 'Cohere',            src: '/images/logos/cohere.svg'  },
  ];
  const rowB = [
    { name: 'Microsoft',         src: '/microsoft-icon.svg'       },
    { name: 'Perplexity',        src: '/images/logos/perplexity.svg' },
    { name: 'AI21',              src: '/ai21-icon.svg'            },
    { name: 'ByteDance',         src: '/bytedance.svg'            },
    { name: 'Black Forest Labs', src: '/flux.svg'                 },
    { name: 'Ideogram',          src: '/ideogram.svg'             },
    { name: 'Runway',            src: '/runway.svg'               },
    { name: 'Suno',              src: '/suno.svg'                 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.7, duration: 0.9 }}
      style={{
        position: 'relative',
        marginTop: isMobile ? '60px' : '96px',
      }}
    >
      {/* Small caption above the strip */}
      <div style={{
        textAlign: 'center',
        marginBottom: isMobile ? '22px' : '32px',
      }}>
        <span style={{
          fontFamily: MONO, fontSize: '10px', letterSpacing: '0.28em',
          color: SP.subtle, fontWeight: 600, textTransform: 'uppercase',
        }}>
          Every frontier, in one workspace
        </span>
      </div>

      {/* Row 1 - drifts left */}
      <LogoRow items={rowA} direction={-1} duration={48} />
      {/* Row 2 - drifts right, tighter gap */}
      <div style={{ height: '14px' }} />
      <LogoRow items={rowB} direction={+1} duration={55} />
    </motion.div>
  );
}

function LogoRow({ items, direction, duration }) {
  const { isMobile, isTablet } = useBreakpoint();
  // Triple the row so the linear translation loops seamlessly.
  const row = [...items, ...items, ...items];
  const tileSize = isMobile ? 56 : isTablet ? 68 : 76;
  const gap = isMobile ? 12 : 18;

  return (
    <div style={{
      position: 'relative',
      overflow: 'hidden',
      padding: isMobile ? '4px 0' : '6px 0',
    }}>
      <motion.div
        animate={{ x: direction < 0 ? ['0%', '-33.333%'] : ['-33.333%', '0%'] }}
        transition={{ duration, repeat: Infinity, ease: 'linear' }}
        style={{
          display: 'flex',
          gap: `${gap}px`,
          whiteSpace: 'nowrap',
          width: 'max-content',
        }}
      >
        {row.map((p, i) => (
          <div
            key={i}
            title={p.name}
            style={{
              flex: `0 0 ${tileSize}px`,
              height: `${tileSize}px`,
              borderRadius: '18px',
              background: 'rgba(255,255,255,0.85)',
              border: `1px solid ${SP.accentTintB}`,
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              boxShadow: '0 10px 28px -18px rgba(124,58,237,0.30)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: tileSize * 0.22 + 'px',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 18px 32px -14px rgba(124,58,237,0.45)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 10px 28px -18px rgba(124,58,237,0.30)';
            }}
          >
            <img
              src={p.src}
              alt={p.name}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        ))}
      </motion.div>

      {/* Edge fades so the strip dissolves into the page */}
      <div style={{
        position: 'absolute', top: 0, left: 0, bottom: 0,
        width: isMobile ? '60px' : '140px',
        background: 'linear-gradient(90deg, #050505 0%, transparent 100%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0,
        width: isMobile ? '60px' : '140px',
        background: 'linear-gradient(-90deg, #050505 0%, transparent 100%)',
        pointerEvents: 'none',
      }} />
    </div>
  );
}

function HeroWord({ i, children }) {
  return (
    <motion.span
      initial={{ opacity: 0, y: 48 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 + i * 0.08, duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
      style={{ display: 'inline-block', marginRight: '0.22em', paddingBottom: '0.18em' }}
    >
      {children}
    </motion.span>
  );
}

function HeroEm({ i, children }) {
  return (
    <motion.em
      initial={{ opacity: 0, y: 48 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 + i * 0.08, duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
      style={{
        fontStyle: 'italic',
        background: SP.accentGrad,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        display: 'inline-block',
        padding: '0 0.06em 0.18em',
        marginRight: '0.1em',
      }}
    >
      {children}
    </motion.em>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STATS MOMENT - natural-height section, category counts animate in.
   No sticky pin, no duplicate provider ticker (lives in the hero carousel).
   ═══════════════════════════════════════════════════════════════ */
function StatsMoment({ stats, loading }) {
  const { isMobile, isTablet } = useBreakpoint();

  return (
    <section style={{
      position: 'relative',
      zIndex: 1,
      padding: isMobile ? '40px 20px 72px' : isTablet ? '60px 32px 96px' : '80px 40px 120px',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div style={{ width: '100%', maxWidth: '1240px' }}>
          <div style={{ marginBottom: isMobile ? '40px' : '56px', textAlign: 'center' }}>
            <EyebrowDot label="THE SURFACE AREA" />
            <h2 style={{
              fontFamily: SERIF,
              fontSize: 'clamp(32px, 5vw, 64px)',
              lineHeight: 1.02,
              letterSpacing: '-0.03em',
              color: SP.ink,
              fontWeight: 400,
              margin: 0,
              paddingBottom: '0.12em',
            }}>
              A platform measured in{' '}
              <em style={{
                fontStyle: 'italic',
                background: SP.accentGrad,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: 'inline-block',
                paddingRight: '0.12em',
                paddingBottom: '0.08em',
              }}>
                choices
              </em>
              , not features.
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: '1px',
            background: SP.line,
            border: `1px solid ${SP.line}`,
            borderRadius: isMobile ? '16px' : '24px',
            overflow: 'hidden',
          }}>
            <StatCell value={stats.chat}  label="Chat models"  delay={0.0} loading={loading} />
            <StatCell value={stats.image} label="Image models" delay={0.1} loading={loading} />
            <StatCell value={stats.video} label="Video models" delay={0.2} loading={loading} />
            <StatCell value={stats.music} label="Music models" delay={0.3} loading={loading} />
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function EyebrowDot({ label }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '18px',
    }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: SP.accentGrad }} />
      <span style={{
        fontFamily: MONO, fontSize: '10px', letterSpacing: '0.28em',
        color: SP.ink, fontWeight: 600, textTransform: 'uppercase',
      }}>
        {label}
      </span>
    </div>
  );
}

function StatCell({ value, label, delay = 0, loading }) {
  const cellRef = useRef(null);
  const inView = useInView(cellRef, { once: true, amount: 0.4 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView || loading) return;
    const start = performance.now();
    const duration = 1600;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, loading]);

  return (
    <motion.div
      ref={cellRef}
      initial={{ opacity: 0, y: 12 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.6 }}
      style={{
        background: 'rgba(255,255,255,0.03)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        padding: '36px 20px',
        textAlign: 'center',
      }}
    >
      <div style={{
        fontFamily: SERIF,
        fontSize: 'clamp(48px, 6.5vw, 86px)',
        lineHeight: 1,
        color: SP.ink,
        fontWeight: 400,
        letterSpacing: '-0.035em',
        marginBottom: '10px',
      }}>
        {loading ? '-' : display}{!loading && value >= 200 ? '+' : ''}
      </div>
      <div style={{
        fontFamily: MONO, fontSize: '10px', letterSpacing: '0.24em',
        color: SP.muted, fontWeight: 600, textTransform: 'uppercase',
      }}>
        {label}
      </div>
    </motion.div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   PROVIDER GALAXY - grid of provider cards with logos & counts.
   ═══════════════════════════════════════════════════════════════ */
function ProviderGalaxy({ buckets, loading }) {
  const { isMobile, isTablet } = useBreakpoint();

  // Curated display order for the galaxy. Top 13 providers.
  const display = useMemo(() => {
    if (loading || !buckets.length) {
      return [
        { provider: 'Anthropic', list: [], count: 13 },
        { provider: 'OpenAI',    list: [], count: 47 },
        { provider: 'Google',    list: [], count: 26 },
        { provider: 'Meta',      list: [], count: 24 },
        { provider: 'DeepSeek',  list: [], count: 9  },
        { provider: 'Mistral',   list: [], count: 15 },
        { provider: 'xAI',       list: [], count: 10 },
        { provider: 'Cohere',    list: [], count: 4  },
        { provider: 'Qwen',      list: [], count: 49 },
        { provider: 'Microsoft', list: [], count: 1  },
        { provider: 'Perplexity',list: [], count: 5  },
        { provider: 'AI21',      list: [], count: 1  },
      ];
    }
    return buckets.slice(0, 12);
  }, [buckets, loading]);

  return (
    <section style={{
      position: 'relative', zIndex: 1,
      maxWidth: '1340px',
      margin: '0 auto',
      padding: isMobile ? '40px 20px 80px' : isTablet ? '60px 32px 100px' : '80px 40px 140px',
    }}>
      {/* Section header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: isMobile ? '20px' : '32px',
        marginBottom: isMobile ? '48px' : '80px',
        paddingBottom: isMobile ? '18px' : '24px',
        borderBottom: `1px solid ${SP.line}`,
        flexWrap: 'wrap',
      }}>
        <div>
          <EyebrowDot label={`THE PROVIDERS · ${display.length} FRONTIERS`} />
          <h2 style={{
            fontFamily: SERIF,
            fontSize: 'clamp(36px, 5vw, 68px)',
            lineHeight: 1.0,
            letterSpacing: '-0.035em',
            color: SP.ink,
            fontWeight: 400,
            margin: 0,
            paddingBottom: '0.1em',
            maxWidth: '820px',
          }}>
            Thirteen labs,{' '}
            <em style={{
              fontStyle: 'italic',
              background: SP.accentGrad,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
              paddingRight: '0.12em',
              paddingBottom: '0.08em',
            }}>
              one login
            </em>
            .
          </h2>
        </div>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic', fontSize: '17px', lineHeight: 1.55,
          color: SP.muted, margin: 0, maxWidth: '380px',
        }}>
          Every model below is available the moment you sign in. No provider-specific
          keys, no separate bills, no switching tabs.
        </p>
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
        gap: isMobile ? '16px' : '24px',
      }}>
        {display.map((b, i) => (
          <ProviderCard key={b.provider} bucket={b} index={i} />
        ))}
      </div>
    </section>
  );
}

function ProviderCard({ bucket, index }) {
  const { isMobile } = useBreakpoint();
  const { provider, list, count } = bucket;
  // Use a checkerboard pattern for the solid gradient cards.
  const isSpotlight = index % 2 === 0 && !isMobile;

  const cardRef = useRef(null);
  const rotX = useMotionValue(0);
  const rotY = useMotionValue(0);
  const spX = useSpring(rotX, { stiffness: 260, damping: 22 });
  const spY = useSpring(rotY, { stiffness: 260, damping: 22 });
  const onMove = (e) => {
    const el = cardRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    rotY.set((px - 0.5) * 5);
    rotX.set(-(py - 0.5) * 5);
  };
  const onLeave = () => { rotX.set(0); rotY.set(0); };

  const logo = PROVIDER_LOGOS[provider];
  // Curated example model names per provider (surfaced even before API loads).
  const topExamples = list.length
    ? list.slice(0, 3).map(m => m.name)
    : {
        Anthropic: ['Claude 4.5 Sonnet', 'Claude 3.7 Sonnet', 'Claude 3.5 Haiku'],
        OpenAI:    ['GPT-5', 'GPT-4o', 'GPT-4o mini'],
        Google:    ['Gemini 3 Pro', 'Gemini 2.5 Pro', 'Imagen 4'],
        Meta:      ['Llama 4 Maverick', 'Llama 3.3 70B', 'Llama 3.1 405B'],
        DeepSeek:  ['DeepSeek V3.2', 'DeepSeek R1'],
        Mistral:   ['Mistral Large 2', 'Codestral'],
        xAI:       ['Grok 4', 'Grok 3'],
        Cohere:    ['Command R+', 'Command R'],
        Qwen:      ['Qwen 3 Max', 'Qwen 2.5 72B'],
        Microsoft: ['Phi-4'],
        Perplexity:['Sonar Pro', 'Sonar'],
        AI21:      ['Jamba 1.5 Large'],
      }[provider] || [];

  return (
    <motion.article
      ref={cardRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'relative',
        minHeight: isMobile ? '300px' : isSpotlight ? '360px' : '320px',
        padding: isMobile ? '26px 24px' : '32px 30px',
        borderRadius: isMobile ? '22px' : '26px',
        background: isSpotlight
          ? 'linear-gradient(155deg, rgba(10,10,10,0.6) 0%, rgba(26,16,51,0.6) 55%, rgba(10,10,26,0.6) 100%)'
          : 'rgba(255,255,255,0.03)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1px solid ${isSpotlight ? 'rgba(167,139,250,0.22)' : SP.line}`,
        boxShadow: isSpotlight
          ? '0 40px 80px -28px rgba(124,58,237,0.50), inset 0 1px 0 rgba(255,255,255,0.05)'
          : '0 4px 24px -1px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'hidden',
        transformPerspective: 1200,
        rotateX: spX,
        rotateY: spY,
      }}
    >
      {/* Background decoration */}
      {isSpotlight && (
        <div style={{
          position: 'absolute', top: '-30%', right: '-10%',
          width: '460px', height: '460px',
          background: 'radial-gradient(circle at 60% 40%, rgba(167,139,250,0.30) 0%, transparent 60%)',
          filter: 'blur(30px)', pointerEvents: 'none',
        }} />
      )}

      {/* Top - mono count tag */}
      <div style={{
        position: 'relative', zIndex: 2,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center',
          padding: '5px 11px', borderRadius: '999px',
          background: isSpotlight ? 'rgba(167,139,250,0.18)' : SP.accentTint,
          border: isSpotlight ? '1px solid rgba(167,139,250,0.20)' : `1px solid ${SP.accentTintB}`,
          fontFamily: MONO, fontSize: '9.5px', letterSpacing: '0.2em',
          color: isSpotlight ? '#C4B5FD' : SP.accentDeep, fontWeight: 700,
        }}>
          {count} MODELS
        </span>
      </div>

      {/* Middle - logo tile + provider name */}
      <div style={{ position: 'relative', zIndex: 2, margin: '24px 0' }}>
        <div style={{
          width: isMobile ? '52px' : isSpotlight ? '64px' : '56px',
          height: isMobile ? '52px' : isSpotlight ? '64px' : '56px',
          borderRadius: isMobile ? '14px' : '16px',
          background: isSpotlight ? 'rgba(255,255,255,0.06)' : SP.canvas,
          border: isSpotlight ? '1px solid rgba(255,255,255,0.08)' : `1px solid ${SP.line}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '22px',
          padding: '12px',
        }}>
          {logo ? (
            <img
              src={logo}
              alt={provider}
              style={{
                width: '100%', height: '100%', objectFit: 'contain',
                filter: isSpotlight ? 'brightness(0) invert(1) opacity(0.9)' : 'none',
              }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: SP.accentGrad, opacity: 0.3 }} />
          )}
        </div>

        <h3 style={{
          fontFamily: SERIF,
          fontSize: isMobile ? '28px' : isSpotlight ? '36px' : '30px',
          lineHeight: 1.02,
          letterSpacing: '-0.028em',
          color: isSpotlight ? '#FFFFFF' : SP.ink,
          fontWeight: 400,
          margin: '0 0 14px',
        }}>
          {provider}
        </h3>

        {/* Example model names — geometric tech face for a machine-register read. */}
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {topExamples.slice(0, 3).map((name) => (
            <li key={name} style={{
              fontFamily: TECH,
              fontSize: '13px',
              lineHeight: 1.7,
              color: isSpotlight ? 'rgba(255,255,255,0.75)' : SP.muted,
              letterSpacing: '0.02em',
              fontWeight: 500,
              textTransform: 'uppercase',
            }}>
              {name}
            </li>
          ))}
        </ul>
      </div>

      {/* Bottom - CTA */}
      <div style={{
        position: 'relative', zIndex: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingTop: '16px',
        borderTop: `1px solid ${isSpotlight ? 'rgba(255,255,255,0.09)' : SP.line}`,
      }}>
        <span style={{
          fontFamily: MONO, fontSize: '10px', letterSpacing: '0.2em',
          color: isSpotlight ? 'rgba(255,255,255,0.5)' : SP.subtle,
          fontWeight: 600, textTransform: 'uppercase',
        }}>
          Available now
        </span>
        <a href="/chat" style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          color: isSpotlight ? '#C4B5FD' : SP.accentA,
          fontFamily: MONO, fontSize: '10px', letterSpacing: '0.2em',
          fontWeight: 700, textTransform: 'uppercase', textDecoration: 'none',
        }}>
          Use
          <span style={{ fontSize: '13px', transform: 'translateY(-1px)' }}>→</span>
        </a>
      </div>
    </motion.article>
  );
}

/* ═══════════════════════════════════════════════════════════════
   LINEUP SECTION - live searchable, tabbed table of every model.
   ═══════════════════════════════════════════════════════════════ */
const TABS = ['All', 'Chat', 'Image', 'Video', 'Music'];

function LineupSection({ models, loading }) {
  const { isMobile, isTablet } = useBreakpoint();
  const [tab, setTab] = useState('All');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    return models.filter(m => {
      if (tab !== 'All' && m.fn !== tab) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return m.name.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q);
    });
  }, [models, tab, query]);

  return (
    <section id="lineup" style={{
      position: 'relative', zIndex: 1,
      maxWidth: '1340px',
      margin: '0 auto',
      padding: isMobile ? '40px 20px 80px' : isTablet ? '60px 32px 100px' : '80px 40px 140px',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: isMobile ? '20px' : '32px',
        marginBottom: isMobile ? '32px' : '56px',
        paddingBottom: isMobile ? '18px' : '24px',
        borderBottom: `1px solid ${SP.line}`,
        flexWrap: 'wrap',
      }}>
        <div>
          <EyebrowDot label={`THE LINEUP · ${models.length || '…'} MODELS`} />
          <h2 style={{
            fontFamily: SERIF,
            fontSize: 'clamp(36px, 5vw, 64px)',
            lineHeight: 1.02,
            letterSpacing: '-0.03em',
            color: SP.ink,
            fontWeight: 400,
            margin: 0,
            paddingBottom: '0.1em',
          }}>
            Search the{' '}
            <em style={{
              fontStyle: 'italic',
              background: SP.accentGrad,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
              paddingRight: '0.12em',
              paddingBottom: '0.08em',
            }}>
              whole library
            </em>
            .
          </h2>
        </div>

        {/* Search */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 16px',
          borderRadius: '999px',
          background: 'rgba(255,255,255,0.03)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${SP.line}`,
          width: isMobile ? '100%' : isTablet ? '100%' : '320px',
          maxWidth: '100%',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}>
          <SearchGlyph />
          <input
            type="text"
            placeholder="Search models or providers"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none', outline: 'none',
              fontFamily: 'inherit',
              fontSize: '14px',
              color: SP.ink,
              background: 'transparent',
              letterSpacing: '-0.01em',
            }}
          />
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '28px',
        flexWrap: 'wrap',
        background: SP.canvas,
        padding: '5px',
        borderRadius: isMobile ? '18px' : '999px',
        border: `1px solid ${SP.line}`,
        width: isMobile ? '100%' : 'fit-content',
        justifyContent: isMobile ? 'center' : 'flex-start',
      }}>
        {TABS.map(t => {
          const active = t === tab;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                position: 'relative',
                padding: '9px 18px',
                borderRadius: '999px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: MONO,
                fontSize: '10.5px',
                fontWeight: 700,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: active ? SP.ink : SP.muted,
                transition: 'color 0.2s',
              }}
            >
              {active && (
                <motion.div
                  layoutId="tabPill"
                  transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${SP.accentTintB}`,
                    borderRadius: '999px',
                    boxShadow: '0 4px 12px -6px rgba(124,58,237,0.25)',
                    zIndex: 0,
                  }}
                />
              )}
              <span style={{ position: 'relative', zIndex: 1 }}>{t}</span>
            </button>
          );
        })}
      </div>

      {/* Table / list */}
      <div style={{
        background: 'rgba(255,255,255,0.02)',
        backdropFilter: 'blur(20px)',
        border: `1px solid ${SP.line}`,
        borderRadius: isMobile ? '18px' : '22px',
        overflow: 'hidden',
      }}>
        {/* Header row - desktop only */}
        {!isMobile && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '40px 2fr 1.1fr 1fr 120px',
            padding: '16px 24px',
            borderBottom: `1px solid ${SP.line}`,
            background: SP.canvas,
            gap: '16px',
            alignItems: 'center',
          }}>
            {['', 'MODEL', 'PROVIDER', 'CONTEXT', 'TYPE'].map((h, i) => (
              <span key={i} style={{
                fontFamily: MONO, fontSize: '9.5px', letterSpacing: '0.22em',
                color: SP.subtle, fontWeight: 700,
                textAlign: h === 'TYPE' ? 'right' : 'left',
              }}>
                {h}
              </span>
            ))}
          </div>
        )}

        {loading ? (
          <LoadingRow />
        ) : filtered.length === 0 ? (
          <EmptyRow />
        ) : (
          <div style={{ maxHeight: '720px', overflowY: 'auto' }}>
            <AnimatePresence initial={false}>
              {filtered.slice(0, 200).map((m, i) => (
                <ModelRow key={m.id + i} model={m} index={i} isMobile={isMobile} />
              ))}
            </AnimatePresence>
            {filtered.length > 200 && (
              <div style={{
                padding: '18px 24px',
                textAlign: 'center',
                fontFamily: MONO,
                fontSize: '10px',
                letterSpacing: '0.2em',
                color: SP.subtle,
                fontWeight: 600,
                textTransform: 'uppercase',
                borderTop: `1px solid ${SP.line}`,
              }}>
                Showing 200 of {filtered.length} · refine your search
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function ModelRow({ model, index, isMobile }) {
  const { provider, name, ctxWindow, inputPerM, fn } = model;
  const logo = PROVIDER_LOGOS[provider];
  const fnColor = { Chat: SP.accentA, Image: '#EC4899', Video: '#F59E0B', Music: '#10B981' }[fn] || SP.accentA;

  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: Math.min(index * 0.015, 0.4) }}
        style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${SP.lineSoft}`,
          display: 'flex', alignItems: 'center', gap: '12px',
        }}
      >
        <div style={{
          width: '36px', height: '36px', borderRadius: '10px',
          background: SP.canvas, border: `1px solid ${SP.line}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '6px', flexShrink: 0,
        }}>
          {logo ? <img src={logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e=>{e.currentTarget.style.display='none';}} /> : null}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: TECH, fontSize: '13px', color: SP.ink, letterSpacing: '0.02em', lineHeight: 1.25, fontWeight: 500, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
            <span style={{ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '0.16em', color: SP.muted, fontWeight: 600, textTransform: 'uppercase' }}>
              {provider}
            </span>
            <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: SP.faint }} />
            <span style={{ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '0.16em', color: fnColor, fontWeight: 700 }}>
              {fn.toUpperCase()}
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.012, 0.4) }}
      style={{
        display: 'grid',
        gridTemplateColumns: '40px 2fr 1.1fr 1fr 120px',
        padding: '14px 24px',
        borderBottom: `1px solid ${SP.lineSoft}`,
        gap: '16px',
        alignItems: 'center',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = SP.canvas; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{
        width: '32px', height: '32px', borderRadius: '9px',
        background: SP.canvas, border: `1px solid ${SP.line}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '6px',
      }}>
        {logo ? <img src={logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e=>{e.currentTarget.style.display='none';}} /> : null}
      </div>
      <span style={{
        fontFamily: TECH, fontSize: '13.5px', color: SP.ink,
        letterSpacing: '0.02em', fontWeight: 500,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {name}
      </span>
      <span style={{
        fontFamily: MONO, fontSize: '11px', letterSpacing: '0.12em',
        color: SP.inkSoft, fontWeight: 600, textTransform: 'uppercase',
      }}>
        {provider}
      </span>
      <span style={{
        fontFamily: MONO, fontSize: '12px', color: SP.muted, fontWeight: 500,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {ctxWindow ? ctxWindow.toLocaleString() : '—'}
      </span>
      <span style={{
        display: 'inline-flex', justifyContent: 'flex-end',
      }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center',
          padding: '4px 10px', borderRadius: '999px',
          background: fnColor + '12',
          fontFamily: MONO, fontSize: '9.5px', letterSpacing: '0.18em',
          color: fnColor, fontWeight: 700,
        }}>
          {fn.toUpperCase()}
        </span>
      </span>
    </motion.div>
  );
}

function LoadingRow() {
  return (
    <div style={{ padding: '60px 24px', textAlign: 'center' }}>
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
        style={{
          display: 'inline-block',
          width: '28px', height: '28px',
          borderRadius: '50%',
          border: `2px solid ${SP.line}`,
          borderTopColor: SP.accentA,
          marginBottom: '18px',
        }}
      />
      <div style={{
        fontFamily: MONO, fontSize: '10px', letterSpacing: '0.24em',
        color: SP.subtle, fontWeight: 600, textTransform: 'uppercase',
      }}>
        Loading the library…
      </div>
    </div>
  );
}

function EmptyRow() {
  return (
    <div style={{ padding: '72px 24px', textAlign: 'center' }}>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic', fontSize: '22px',
        color: SP.muted, letterSpacing: '-0.02em', marginBottom: '8px',
      }}>
        Nothing matches that.
      </div>
      <div style={{
        fontFamily: MONO, fontSize: '10px', letterSpacing: '0.22em',
        color: SP.subtle, fontWeight: 600, textTransform: 'uppercase',
      }}>
        Try another term or switch tabs
      </div>
    </div>
  );
}

function SearchGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke={SP.subtle} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MANIFESTO & CTA - mirror SolutionsPage cadence.
   ═══════════════════════════════════════════════════════════════ */
function Manifesto() {
  const { isMobile, isTablet } = useBreakpoint();
  return (
    <section style={{
      position: 'relative', zIndex: 1,
      maxWidth: '1240px',
      margin: '0 auto',
      padding: isMobile ? '60px 20px 100px' : isTablet ? '70px 32px 130px' : '80px 40px 160px',
      textAlign: 'center',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        <div style={{ marginBottom: '32px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: SP.accentGrad }} />
          <span style={{
            fontFamily: MONO, fontSize: '10px', letterSpacing: '0.28em',
            color: SP.ink, fontWeight: 600, textTransform: 'uppercase',
          }}>
            The north star
          </span>
        </div>

        <h2 style={{
          fontFamily: SERIF,
          fontSize: 'clamp(40px, 6.4vw, 92px)',
          lineHeight: 1.02,
          letterSpacing: '-0.035em',
          color: SP.ink,
          fontWeight: 400,
          margin: 0, paddingBottom: '0.1em',
        }}>
          The{' '}
          <em style={{ fontStyle: 'italic', background: SP.accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block', paddingRight: '0.12em', paddingBottom: '0.08em' }}>
            right model
          </em>
          , for the{' '}
          <em style={{ fontStyle: 'italic', background: SP.accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block', paddingRight: '0.12em', paddingBottom: '0.08em' }}>
            right job
          </em>
          .
        </h2>

        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: '20px', lineHeight: 1.55, color: SP.muted,
          margin: '36px auto 0', maxWidth: '620px',
        }}>
          Not a pile of models you have to manage. A platform that knows which
          one to reach for - and gets out of the way.
        </p>
      </motion.div>
    </section>
  );
}

function CtaSection({ onSignInClick }) {
  const { isMobile, isTablet } = useBreakpoint();
  return (
    <section style={{
      position: 'relative', zIndex: 1,
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 20px 100px' : isTablet ? '10px 32px 140px' : '20px 40px 180px',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'relative',
          padding: isMobile ? '56px 24px' : isTablet ? '72px 48px' : '88px 64px',
          borderRadius: isMobile ? '24px' : '36px',
          background: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(8px)',
          border: `1px solid ${SP.accentTintB}`,
          overflow: 'hidden',
          textAlign: 'center',
          boxShadow: '0 48px 120px -40px rgba(124,58,237,0.28)',
        }}
      >
        <div style={{ position: 'absolute', top: '-50%', left: '-20%', width: '580px', height: '580px',
          background: 'radial-gradient(circle, rgba(167,139,250,0.38) 0%, transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-55%', right: '-20%', width: '620px', height: '620px',
          background: 'radial-gradient(circle, rgba(196,181,253,0.32) 0%, transparent 65%)', pointerEvents: 'none' }} />

        <div style={{
          position: 'relative',
          display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '26px',
        }}>
          <motion.span
            animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            style={{ width: '6px', height: '6px', borderRadius: '50%', background: SP.accentGrad }}
          />
          <span style={{
            fontFamily: MONO, fontSize: '10px', letterSpacing: '0.28em',
            color: SP.accentDeep, fontWeight: 600, textTransform: 'uppercase',
          }}>
            Ready when you are
          </span>
        </div>

        <h2 style={{
          position: 'relative',
          fontFamily: SERIF,
          fontSize: 'clamp(42px, 6vw, 80px)',
          lineHeight: 1.0,
          letterSpacing: '-0.035em',
          color: SP.ink,
          fontWeight: 400,
          margin: '0 auto 22px', maxWidth: '840px',
          paddingBottom: '0.1em',
        }}>
          Every model, in{' '}
          <em style={{ fontStyle: 'italic', background: SP.accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block', paddingRight: '0.12em', paddingBottom: '0.08em' }}>
            one tab
          </em>
          .
        </h2>

        <p style={{
          position: 'relative',
          fontSize: '18px', lineHeight: 1.6, color: SP.muted,
          maxWidth: '580px', margin: '0 auto 44px',
        }}>
          One DAGGPT login opens the full library. No new tools to learn,
          no subscriptions to stack - just the right model, for the right job.
        </p>

        <div style={{
          position: 'relative',
          display: 'inline-flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center',
        }}>
          <PrimaryCTA href="/chat">Open the workspace</PrimaryCTA>
          <GhostCTA onClick={onSignInClick} trailing="→">Sign in to save work</GhostCTA>
        </div>

        <div style={{
          position: 'relative',
          display: 'flex', justifyContent: 'center',
          gap: isMobile ? '14px' : '32px',
          marginTop: isMobile ? '28px' : '44px',
          flexWrap: 'wrap', rowGap: '12px',
        }}>
          {['Pay as you go', 'Switch models anytime', 'One unified bill'].map(t => (
            <div key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: SP.accentA, fontFamily: MONO, fontSize: '11px', fontWeight: 700 }}>✓</span>
              <span style={{
                fontFamily: MONO, fontSize: '10px', letterSpacing: '0.18em',
                color: SP.muted, fontWeight: 600, textTransform: 'uppercase',
              }}>
                {t}
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

/* ─── Shared CTAs (matches SolutionsPage) ─────────────────────── */
function PrimaryCTA({ children, href, onClick }) {
  const style = {
    display: 'inline-flex', alignItems: 'center',
    padding: '16px 34px', borderRadius: '999px',
    background: SP.accentGrad, color: '#FFFFFF',
    fontFamily: MONO, fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.24em', textTransform: 'uppercase',
    textDecoration: 'none', cursor: 'pointer', border: 'none',
    boxShadow: '0 14px 32px -14px rgba(124,58,237,0.55)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  };
  const onEnter = e => {
    e.currentTarget.style.transform = 'translateY(-1px)';
    e.currentTarget.style.boxShadow = '0 20px 40px -16px rgba(124,58,237,0.7)';
  };
  const onLeave = e => {
    e.currentTarget.style.transform = 'none';
    e.currentTarget.style.boxShadow = '0 14px 32px -14px rgba(124,58,237,0.55)';
  };
  if (href) return <a href={href} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>{children}</a>;
  return <button onClick={onClick} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>{children}</button>;
}

function GhostCTA({ children, trailing, href, onClick }) {
  const style = {
    display: 'inline-flex', alignItems: 'center', gap: '12px',
    padding: '16px 32px', borderRadius: '999px',
    background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(20px)', color: SP.ink,
    border: `1px solid ${SP.line}`,
    fontFamily: MONO, fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.24em', textTransform: 'uppercase',
    textDecoration: 'none', cursor: 'pointer',
    transition: 'background 0.2s, border 0.2s, transform 0.2s',
  };
  const onEnter = e => {
    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
    e.currentTarget.style.borderColor = SP.accentTintB;
    e.currentTarget.style.transform = 'translateY(-1px)';
  };
  const onLeave = e => {
    e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
    e.currentTarget.style.transform = 'none';
  };
  const content = (
    <>
      <span>{children}</span>
      {trailing && <span style={{ fontSize: '14px', letterSpacing: 0, transform: 'translateY(-1px)' }}>{trailing}</span>}
    </>
  );
  if (href) return <a href={href} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>{content}</a>;
  return <button onClick={onClick} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>{content}</button>;
}
